import { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import mixpanel from 'mixpanel-browser';
import Dialog from '../Dialog';
import Input from '../Input';
import Button from '../Button';
import Checkbox from './Checkbox';
import PasswordStrengthIndicator from '../PasswordStrengthIndicator';
import { PasswordStrength } from '../PasswordStrengthIndicator/PasswordStrengthIndicator';

const ADD_PLAYER_CLICK_EVENT_NAME = 'Therapy_RestoreSkillsUI_MyPlayers_Click';
const SCREEN_BREAKPOINT = '1100px';

const DialogButton = styled(Button)`
  padding: 15px 60px;
`;

const CancelButton = styled.button(({ theme }) => {
  return `
    font-size: ${theme.type.size.small};
    color: ${theme.color.grayscale[0]};
    background: none;
    border: none;
    cursor: pointer;
    padding: 15px 40px;
    margin-right: 16px;
    `;
});

const GeneralErrorList = styled.div(({ theme }) => {
  return `
    margin-top: 10px;
    font-size: ${theme.type.size.regular};
    color: ${theme.color.primary.dark};
  `;
});

const Title = styled.div(({ theme }) => {
  return `
    font-size: ${theme.type.size.larger};
    font-weight: bold;
    margin-bottom: 20px;

    @media (max-width: ${SCREEN_BREAKPOINT}) {
      font-size: ${theme.type.size.large}
    }
  `;
});

const InputVariant = styled(Input)`
  padding: 0.625rem;
`;

const Content = styled.div`
  width: 800px;
  @media (max-width: ${SCREEN_BREAKPOINT}) {
    width: 700px;
  }

  @media (max-width: 700px) {
    width: 100%;
  }
`;

const InputContainer = styled.div`
  margin-bottom: 20px;
  text-align: left;
  width: 300px;

  @media (max-width: ${SCREEN_BREAKPOINT}) {
    width: 100%;
  }
`;

const Section = styled.div(({ theme }) => {
  return `
    border: 1px solid ${theme.color.grayscale[700]};
    border-radius: 10px;
    padding: 10px 45px;
    width: 726px;
    margin: 0 auto;
    margin-bottom: 40px;

    @media (max-width: ${SCREEN_BREAKPOINT}) {
      width: 100%;
    }
  `;
});

const InputSectionContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 35px;
  min-width: 400px;
  justify-content: center;

  @media (max-width: ${SCREEN_BREAKPOINT}) {
    min-width: inherit;
    display: contents;
    width: 100%;
  }
`;

const SectionTitle = styled.div(({ theme }) => {
  return `
    font-weight: bold;
    font-size: ${theme.type.size.large};
    margin-bottom: 15px;
    text-align: left;
    position: relative;
    margin-top: 10px;
  `;
});

const CheckboxContainer = styled.label`
  display: flex;
  align-items: center;
  margin-bottom: -5px;
`;

const ButtonWrap = styled.div`
  margin-right: 10px;
  display: inline-block;
  cursor: pointer;
`;

export type FormErrors = {
  firstName?: string;
  lastName?: string;
  login?: string;
  password?: string;
  passwordConfirmation?: string;
  general?: string[];
};

interface AddPlayerDialogProps {
  submitCallback?: (
    firstName: string,
    lastName: string,
    shouldAddHomePlayer: boolean,
    login: string,
    password: string
  ) => void;
  backendErrors?: FormErrors;
  isOpen: boolean;
  closeCallback: () => void;
  saving: boolean;
}

const AddPlayerDialog: FC<AddPlayerDialogProps> = ({
  isOpen,
  closeCallback,
  submitCallback,
  saving,
  backendErrors = {},
}) => {
  const [addHomePlayerChecked, setAddHomePlayerChecked] = useState(true);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [passwordStrength, setPasswordStrength] = useState(PasswordStrength.Weak);
  const [canSubmit, setCanSubmit] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const allErrors = Object.assign({}, errors, backendErrors);

  // isQuiet can be used to validate the form without triggering errors.
  const validate = (isQuiet = false) => {
    const newErrors: FormErrors = {};
    let valid = true;

    if (firstName.trim().length < 1) {
      newErrors.firstName = 'First Name cannot be empty';
      valid = false;
    }

    if (lastName.trim().length < 1) {
      newErrors.lastName = 'Last Name cannot be empty';
      valid = false;
    }

    if (addHomePlayerChecked) {
      if (passwordStrength === PasswordStrength.Weak) {
        newErrors.password = 'A weak password is not allowed';
        valid = false;
      }

      if (passwordConfirmation !== password) {
        newErrors.passwordConfirmation = 'Passwords must match';
        valid = false;
      }

      if (login.trim().length < 3) {
        newErrors.login = 'Username must be at least 3 characters long';
        valid = false;
      }
    }

    if (!isQuiet) {
      setErrors(newErrors);
    }

    setCanSubmit(valid);

    return valid;
  };

  const submit = () => {
    const valid = validate();

    if (valid) {
      if (submitCallback) {
        submitCallback(firstName, lastName, addHomePlayerChecked, login, password);
      }

      mixpanel.track(ADD_PLAYER_CLICK_EVENT_NAME, { Action: 'submit' });
    }
  };

  useEffect(() => {
    validate(true);
  }, [addHomePlayerChecked, passwordStrength, firstName, lastName]);

  useEffect(() => {
    if (passwordConfirmation !== '') {
      validate();
    }
  }, [passwordConfirmation]);

  const handleCheckboxChange = (newCheckedStatus: boolean) => {
    if (!newCheckedStatus) {
      setLogin('');
      setPassword('');
    }

    setAddHomePlayerChecked(newCheckedStatus);
  };

  return (
    <Dialog isOpen={isOpen} canClose={false} closeCallback={closeCallback}>
      <Content>
        <Title>Add a Player</Title>

        <Section>
          <SectionTitle>Personal Information</SectionTitle>

          <InputSectionContainer>
            <InputContainer>
              <InputVariant
                label="First Name"
                hideOptional={true}
                required={true}
                value={firstName}
                onChange={(e) => {
                  setFirstName(e.target.value);
                }}
                invalid={allErrors.firstName !== undefined}
                errors={allErrors.firstName}
              />
            </InputContainer>

            <InputContainer>
              <InputVariant
                label="Last Name"
                hideOptional={true}
                required={true}
                onChange={(e) => {
                  setLastName(e.target.value);
                }}
                value={lastName}
                invalid={allErrors.lastName !== undefined}
                errors={allErrors.lastName}
              />
            </InputContainer>
          </InputSectionContainer>
        </Section>

        <Section>
          <CheckboxContainer>
            <SectionTitle>
              <Checkbox updateFunc={handleCheckboxChange} label="Grant Remote Access" defaultValue={true} />
            </SectionTitle>
          </CheckboxContainer>

          <InputSectionContainer>
            <InputContainer>
              <InputVariant
                label="Username"
                hideOptional={true}
                required={true}
                onChange={(e) => {
                  setLogin(e.target.value);
                }}
                value={login}
                invalid={allErrors.login !== undefined}
                errors={allErrors.login}
                disabled={!addHomePlayerChecked}
              />
            </InputContainer>
          </InputSectionContainer>

          <InputSectionContainer>
            <InputContainer>
              <InputVariant
                label="Password"
                hideOptional={true}
                required={true}
                type="password"
                onChange={(e) => {
                  setPassword(e.target.value.trim());
                }}
                value={password}
                invalid={allErrors.password !== undefined}
                errors={allErrors.password}
                disabled={!addHomePlayerChecked}
              />

              {addHomePlayerChecked && password != '' && (
                <PasswordStrengthIndicator
                  password={password}
                  onPasswordStrengthChange={(newPasswordChange) => {
                    setPasswordStrength(newPasswordChange);
                  }}
                />
              )}
            </InputContainer>

            <InputContainer>
              <InputVariant
                label="Confirm Password"
                hideOptional={true}
                required={true}
                type="password"
                onChange={(e) => {
                  setPasswordConfirmation(e.target.value.trim());
                }}
                value={passwordConfirmation}
                invalid={allErrors.passwordConfirmation !== undefined}
                errors={allErrors.passwordConfirmation}
                disabled={!addHomePlayerChecked}
              />
            </InputContainer>
          </InputSectionContainer>
        </Section>

        {!saving && (
          <div>
            <ButtonWrap>
              <CancelButton
                onClick={() => {
                  closeCallback();
                  mixpanel.track(ADD_PLAYER_CLICK_EVENT_NAME, { Action: 'cancel' });
                }}
              >
                Cancel
              </CancelButton>
            </ButtonWrap>

            <ButtonWrap>
              <DialogButton
                disabled={!canSubmit}
                onClick={() => {
                  submit();
                }}
              >
                Submit
              </DialogButton>
            </ButtonWrap>
          </div>
        )}

        {saving && (
          <div>
            Saving... <i className="fa fa-spin fa-spinner"> </i>
          </div>
        )}

        {allErrors.general && (
          <GeneralErrorList>
            {allErrors.general.map((error) => {
              return <div>{error}</div>;
            })}
          </GeneralErrorList>
        )}
      </Content>
    </Dialog>
  );
};

export default AddPlayerDialog;
